const GA_MEASUREMENT_ID = "G-6EKTTYZDG0";

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: Gtag;
    dataLayer?: unknown[];
  }
}

// gtag.js itself is a static <script async> at the end of public/index.html
// (so crawlers see the tag in the HTML); before the visitor answers the cookie
// banner the Consent Mode default keeps it cookieless and hitless. Commands
// pushed here are queued by the inline gtag stub and picked up when it loads.
let analyticsInitialized = false;
let lastConsentState: boolean | null = null;
let lastTrackedPath: string | null = null;

function isBrowserRuntime(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    !navigator.webdriver &&
    typeof (window as Window & { snapSaveState?: unknown }).snapSaveState !== "function"
  );
}

function hasAnalyticsConsent(): boolean {
  try {
    return window.localStorage.getItem("cookie-consent") === "accepted";
  } catch {
    return false;
  }
}

function callGtag(...args: unknown[]): void {
  if (typeof window.gtag === "function") {
    window.gtag(...args);
  }
}

function broadcastConsent(granted: boolean): void {
  if (lastConsentState === granted) {
    return;
  }
  lastConsentState = granted;
  // ad_storage stays denied: the site runs no ads and no remarketing, and
  // granting it made every pageview fire an extra hit to
  // stats.g.doubleclick.net with an ecid for a feature that is not used.
  callGtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
    ad_storage: "denied",
  });
}

export function initializeAnalytics(): void {
  if (!isBrowserRuntime() || !hasAnalyticsConsent() || analyticsInitialized) {
    return;
  }
  broadcastConsent(true);
  // send_page_view is disabled because AppContent sends the one SPA pageview.
  callGtag("js", new Date());
  callGtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
  analyticsInitialized = true;
}

export function setAnalyticsConsent(choice: "accepted" | "declined"): void {
  if (typeof window === "undefined") {
    return;
  }
  if (choice === "declined") {
    broadcastConsent(false);
    analyticsInitialized = false;
    lastTrackedPath = null;
    return;
  }
  initializeAnalytics();
  trackPageView();
}

export function trackPageView(
  pathname: string = window.location.pathname,
): void {
  if (!isBrowserRuntime() || !hasAnalyticsConsent()) {
    return;
  }
  initializeAnalytics();
  if (!analyticsInitialized || lastTrackedPath === pathname) {
    return;
  }
  lastTrackedPath = pathname;
  callGtag("event", "page_view", {
    page_path: pathname,
    page_location: `${window.location.origin}${pathname}`,
    page_title: document.title,
    send_to: GA_MEASUREMENT_ID,
  });
}

export function trackGenerateLead(formType: "quote" | "contact" | "callback"): void {
  if (!isBrowserRuntime() || !hasAnalyticsConsent()) {
    return;
  }
  initializeAnalytics();
  if (!analyticsInitialized) {
    return;
  }
  // Deliberately exclude all form fields: no names, contact details, or trip data.
  callGtag("event", "generate_lead", {
    form_type: formType,
    send_to: GA_MEASUREMENT_ID,
  });
}
